import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormArray, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BackOfficeService } from '../../../../../services/backoffice.service';

@Component({
  selector: 'app-time-slots-modal',
  templateUrl: './time-slots-modal.component.html',
  styleUrls: ['./time-slots-modal.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class TimeSlotsModalComponent {
  timeSlotsForm: UntypedFormGroup;

  constructor(
    private fb: UntypedFormBuilder,
    private backofficeService: BackOfficeService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<TimeSlotsModalComponent>,
  ) {
    const parsedName = this.parseTemplateName(this.data.template?.name);
    const parsedDescription = this.parseTemplateDescription(this.data.template?.description);

    this.timeSlotsForm = this.fb.group({
      name: [parsedName.en || '', Validators.required],
      nameSv: [parsedName.sv || '', Validators.required],
      description: [parsedDescription.en || ''],
      descriptionSv: [parsedDescription.sv || ''],
      timeSlots: this.fb.array(this.data.template?.slots?.length ?
        this.data.template.slots.map(slot => this.createTimeSlotGroupFromData(slot)) :
        [])
    });
  }

  get timeSlots() {
    return this.timeSlotsForm.get('timeSlots') as UntypedFormArray;
  }

  createTimeSlotGroup() {
    return this.fb.group({
      time: ['', Validators.required],
      capacity: [1, [Validators.required, Validators.min(1)]]
    });
  }

  createTimeSlotGroupFromData(slot: any) {
    const formattedTime = slot.Time ? this.formatTimeForInput(slot.Time) : '';
    return this.fb.group({
      time: [formattedTime, Validators.required],
      capacity: [slot.Capacity || 1, [Validators.required, Validators.min(1)]]
    });
  }

  formatTimeForInput(time: string): string {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    return `${hours.padStart(2, '0')}:${minutes || '00'}`;
  }

  private parseTemplateName(name: any): { en: string, sv: string } {
    if (!name) return { en: '', sv: '' };
    try {
      if (typeof name === 'string') {
        const parsed = JSON.parse(name);
        if (parsed && typeof parsed === 'object' && (parsed.en || parsed.sv)) {
          return { en: parsed.en || '', sv: parsed.sv || '' };
        }
        return { en: name, sv: '' };
      } else if (typeof name === 'object') {
        return { en: name.en || '', sv: name.sv || '' };
      } else {
        return { en: String(name), sv: '' };
      }
    } catch (e) {
      return { en: name, sv: '' };
    }
  }

  // private parseTemplateDescription(desc: any): { en: string, sv: string } {
  //   if (!desc) return { en: '', sv: '' };
  //   try {
  //     if (typeof desc === 'string') {
  //       const parsed = JSON.parse(desc);
  //       if (parsed && typeof parsed === 'object' && (parsed.en || parsed.sv)) {
  //         return { en: parsed.en || '', sv: parsed.sv || '' };
  //       }
  //       return { en: desc, sv: '' };
  //     } else if (typeof desc === 'object') {
  //       return { en: desc.en || '', sv: desc.sv || '' };
  //     } else {
  //       return { en: String(desc), sv: '' };
  //     }
  //   } catch (e) {
  //     return { en: desc, sv: '' };
  //   }
  // }

  private parseTemplateDescription(desc: any): { en: string; sv: string } {
    if (!desc) return { en: '', sv: '' };

    try {
      // Case: string (possibly JSON)
      if (typeof desc === 'string') {
        const parsed = JSON.parse(desc);

        if (parsed && typeof parsed === 'object') {
          const en = parsed.en?.trim() || '';
          const sv = parsed.sv?.trim() || '';

          // ✅ BOTH EMPTY → treat as no description
          if (!en && !sv) {
            return { en: '', sv: '' };
          }

          return { en, sv };
        }

        // Plain string (non-JSON)
        return { en: desc, sv: '' };
      }

      // Case: already an object
      if (typeof desc === 'object') {
        const en = desc.en?.trim() || '';
        const sv = desc.sv?.trim() || '';

        if (!en && !sv) {
          return { en: '', sv: '' };
        }

        return { en, sv };
      }

      // Fallback
      return { en: String(desc), sv: '' };

    } catch {
      return { en: '', sv: '' };
    }
  }

  addTimeSlot() {
    this.timeSlots.push(this.createTimeSlotGroup());
  }

  removeTimeSlot(index: number) {
    this.timeSlots.removeAt(index);
  }

  save() {
    if (this.timeSlotsForm.valid) {
      const nameEn = this.timeSlotsForm.get('name')?.value || '';
      const nameSv = this.timeSlotsForm.get('nameSv')?.value || '';
      const nameObj = { en: nameEn, sv: nameSv };

      const descriptionEn = this.timeSlotsForm.get('description')?.value || '';
      const descriptionSv = this.timeSlotsForm.get('descriptionSv')?.value || '';
      const descriptionObj = { en: descriptionEn, sv: descriptionSv };

      const data = {
        id: this.data.template?.id,
        entityId: this.data.selectedServiceId,
        name: JSON.stringify(nameObj),
        description: JSON.stringify(descriptionObj),
        timeSlots: this.timeSlots.value.map(slot => ({
          time: slot.time,
          capacity: slot.capacity
        }))
      };

      this.backofficeService.addUpdateScheduleAssignment(data).subscribe({
        next: (response) => {
          this.dialogRef.close({ success: true, template: response });
        },
        error: (error) => {
          console.error('Error saving schedule assignment:', error);
        }
      });
    }
  }
}